import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { FaTimes, FaArrowLeft, FaCoins, FaSignOutAlt, FaDollarSign, FaSync } from "react-icons/fa";
import socket from "./socket";
import "./Style.css";

export default function DealerDashboard() {
  const navigate = useNavigate();
  const { cardroom } = useParams();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  // ── Core data ──────────────────────────────────────────────
  const [tables, setTables] = useState([]);
  const [players, setPlayers] = useState([]);

  // ── Navigation ─────────────────────────────────────────────
  const [selectedTable, setSelectedTable] = useState(null);

  // ── Chips modal ────────────────────────────────────────────
  // lockedPlayer: when opened from FaDollarSign, player is pre-filled & locked
  const [chipsModal, setChipsModal] = useState({ open: false, lockedPlayer: null });
  const [chipsAmount, setChipsAmount] = useState("");
  const [chipsPlayer, setChipsPlayer] = useState(null);

  // ── Remove confirm modal ───────────────────────────────────
  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState(null);

  // ── Misc ───────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Auth + Axios interceptor ───────────────────────────────
  useEffect(() => {
    if (!token || dealerMode === 0) {
      navigate(`/${cardroom}/`);
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, dealerMode, cardroom, navigate]);

  // ── Socket ─────────────────────────────────────────────────
  useEffect(() => {
    if (!cardroom) return;
    socket.io.opts.query = { cardroom };
    socket.connect();
    return () => socket.disconnect();
  }, [cardroom]);

  // ── Load tables ────────────────────────────────────────────
  useEffect(() => {
    if (!token || dealerMode === 0) return;
    axios
      .get(`/${cardroom}/api/tables`)
      .then(({ data }) => setTables(data))
      .catch((err) => console.error("Failed to load tables", err));
  }, [cardroom, token, dealerMode]);

  // ── Load players when a table is selected ─────────────────
  const loadPlayers = useCallback(
    (tableId) => {
      axios
        .get(`/${cardroom}/api/tables/${tableId}/players`)
        .then(({ data }) => setPlayers(data))
        .catch(() => setPlayers([]));
    },
    [cardroom]
  );

  useEffect(() => {
    if (selectedTable) {
      loadPlayers(selectedTable.id);
    } else {
      setPlayers([]);
    }
  }, [selectedTable, loadPlayers]);

  // ── Helpers ────────────────────────────────────────────────
  const tableLabel = (t) =>
    t.name ||
    `${t.game_type || ""} ${t.stakes || ""}`.trim() ||
    `Table ${t.id}`;

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate(`/`)
  };

  // ── Open chips modal ────────────────────────────────────────
  const openChipsModal = (lockedPlayer = null) => {
    setChipsAmount("");
    // Store just the player id as a string
    setChipsPlayer(lockedPlayer ? String(lockedPlayer.id) : "");
    setChipsModal({ open: true, lockedPlayer });
  };

  const closeChipsModal = () => {
    setChipsModal({ open: false, lockedPlayer: null });
    setChipsAmount("");
    setChipsPlayer(null);
  };

  // ── Submit chips request ───────────────────────────────────
  const handleChipsSubmit = async () => {
    if (!chipsAmount || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Look up name from the players array using the stored id
      const selectedP = players.find((p) => String(p.id) === chipsPlayer);
      await axios.post(`/${cardroom}/api/dealer/chips-request`, {
        amount: parseFloat(chipsAmount),
        playerName: selectedP ? selectedP.name : null,
        tableDescription: selectedTable ? tableLabel(selectedTable) : "",
      });
      closeChipsModal();
    } catch (err) {
      console.error("Chips request failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Remove player ──────────────────────────────────────────
  const handleRemoveConfirm = async () => {
    if (!confirmRemovePlayer || !selectedTable || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.delete(
        `/${cardroom}/api/tables/${selectedTable.id}/unseat/${confirmRemovePlayer.id}?notify=1`
      );
      setConfirmRemovePlayer(null);
      loadPlayers(selectedTable.id);
    } catch (err) {
      console.error("Remove player failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const playerOptions = players.map((p) => ({ value: p.id, label: p.name }));
  // session_credit can come back as a string or null from MySQL — parse safely
  const creditOf = (p) => parseFloat(p?.session_credit ?? 0) || 0;
  const hasCredit = (p) => creditOf(p) > 0;

  // ══════════════════════════════════════════════════════════
  // VIEW 1 — Table list
  // ══════════════════════════════════════════════════════════
  if (!selectedTable) {
    return (
      <div className="game" style={{ minHeight: "100dvh", padding: "1.25rem 1rem" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
            <div>
              <h1 className="game-title" style={{ marginBottom: "0.1rem" }}>
                Dealer Dashboard
              </h1>
              <p style={{ color: "var(--game-text-muted)", fontSize: "0.88rem", margin: 0 }}>
                Select a table to manage
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <button
                className="game-icon-btn"
                onClick={() =>
                  axios.get(`/${cardroom}/api/tables`).then(({ data }) => setTables(data))
                }
                title="Refresh tables"
              >
                <FaSync />
              </button>

                          {dealerMode === 1 && (
              <button
                className="game-btn game-btn--secondary"
                onClick={() => navigate(`/${cardroom}/Game`)}
              >
                Home
              </button>
              
            )}
                          <button
              className="game-btn game-btn--ghost"
                onClick={handleLogout}
                title="Logout"
              style={{ gap: "0.4rem", color: "var(--game-danger)" }}
              >
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </div>

          {tables.length === 0 && (
            <p className="game-empty">No active tables right now.</p>
          )}

          <div className="dd-table-grid">
            {tables.map((t) => (
              <button
                key={t.id}
                className="dd-table-card"
                onClick={() => setSelectedTable(t)}
              >
                <span className="dd-table-name">{tableLabel(t)}</span>
                {t.stakes && t.name && (
                  <span className="dd-table-meta">
                    {t.game_type} · {t.stakes}
                  </span>
                )}
                <span className="dd-table-count">
                  {t.playing_count ?? 0} player
                  {t.playing_count !== 1 ? "s" : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // VIEW 2 — Player list for selected table
  // ══════════════════════════════════════════════════════════
  return (
    <div className="game" style={{ minHeight: "100dvh", padding: "1.25rem 1rem" }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>

        {/* ── Table title + reload ────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem" }}>
          <h1 className="game-title" style={{ margin: 0, fontSize: "1.3rem", flex: 1 }}>
            {tableLabel(selectedTable)}
          </h1>
          <button
            className="game-icon-btn"
            onClick={() => loadPlayers(selectedTable.id)}
            title="Refresh players"
          >
            <FaSync />
          </button>
        </div>

        {/* ── Action row ──────────────────────────────────── */}
        <div className="dd-header-row">
          <button
            className="game-btn game-btn--secondary"
            onClick={() => setSelectedTable(null)}
            style={{ gap: "0.4rem" }}
          >
            <FaArrowLeft /> Back
          </button>

          <button
            className="game-btn game-btn--primary"
            onClick={() => openChipsModal(null)}
            style={{ gap: "0.4rem", marginLeft: "auto" }}
          >
            <FaCoins /> Request Chips
          </button>
        </div>

        {/* ── Player list ─────────────────────────────────── */}
        {players.length === 0 ? (
          <p className="game-empty">No players at this table.</p>
        ) : (
          <ul className="dd-player-list">
            {players.map((p) => (
              <li key={p.id} className="dd-player-row">
                <span className="dd-player-name">{p.name}</span>
                {hasCredit(p) && (
                  <span
                    className="game-badge game-badge--session-yellow"
                    title="Player has session credit"
                  >
                    On Credit
                  </span>
                )}
                {/* Per-player chips request */}
                <button
                  className="game-icon-btn game-icon-btn--success"
                  onClick={() => openChipsModal(p)}
                  aria-label={`Request chips for ${p.name}`}
                  title="Request chips"
                >
                  <FaDollarSign />
                </button>
                {/* Remove player */}
                <button
                  className="game-icon-btn game-icon-btn--danger"
                  onClick={() => setConfirmRemovePlayer(p)}
                  aria-label={`Remove ${p.name}`}
                  title="Remove from table"
                >
                  <FaTimes />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          CHIPS REQUEST MODAL
          ══════════════════════════════════════════════════════ */}
      {chipsModal.open && (
        <>
          <div className="game-modal-overlay" onClick={closeChipsModal} />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                <FaCoins style={{ marginRight: "0.4rem" }} />
                Request Chips for {tableLabel(selectedTable)}
              </h3>
            </div>
            <div className="game-modal__body">
              <div className="game-form-vertical">
                <div>
                  <label className="game-label">Amount ($)</label>
                  <input
                    className="game-input"
                    style={{ width: "100%", boxSizing: "border-box" }}
                    type="number"
                    placeholder="e.g. 200"
                    value={chipsAmount}
                    onChange={(e) => setChipsAmount(e.target.value)}
                    min="1"
                  />
                </div>

                <div>
                  <label className="game-label">Player (optional)</label>
                  <select
                    className="game-input"
                    style={{ width: "100%", boxSizing: "border-box" }}
                    value={chipsPlayer}
                    onChange={(e) => setChipsPlayer(e.target.value)}
                    disabled={!!chipsModal.lockedPlayer}
                  >
                    <option value="">-</option>
                    {players.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    className="game-btn game-btn--secondary"
                    style={{ flex: 1 }}
                    onClick={closeChipsModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="game-btn game-btn--primary"
                    style={{ flex: 1 }}
                    onClick={handleChipsSubmit}
                    disabled={!chipsAmount || isSubmitting}
                  >
                    {isSubmitting ? "Sending…" : "Send Request"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════
          REMOVE PLAYER CONFIRM MODAL
          ══════════════════════════════════════════════════════ */}
      {confirmRemovePlayer && (
        <>
          <div className="game-modal-overlay" onClick={() => setConfirmRemovePlayer(null)} />
          <div className="game-modal game-modal--transaction" style={{ border: "2px solid var(--game-danger)" }}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Remove {confirmRemovePlayer.name} from {tableLabel(selectedTable)}
              </h3>
            </div>
            <div className="game-modal__body">
{hasCredit(confirmRemovePlayer) ? (
  <div style={{ marginBottom: "0.75rem" }}>
    <span
      className="game-player-badge game-player-badge--session-yellow"
      style={{ fontSize: "0.85rem" }}
    >
      <b>${creditOf(confirmRemovePlayer).toFixed(2)}</b>&nbsp;Credit
    </span>
  </div>
) : (
  <div style={{ marginBottom: "0.75rem" }}>
    <span
      className="game-player-badge game-player-badge--credit"
      style={{ fontSize: "0.85rem" }}
    >
      <b>No Session Credit Remaining</b>
    </span>
  </div>
)}

              {hasCredit(confirmRemovePlayer) ? (
                <p style={{ marginBottom: "1.5rem", color: "var(--game-text-secondary)" }}>
                  ⚠️ <strong style={{ color: "var(--game-warning)" }}>{confirmRemovePlayer.name}</strong> has an outstanding session credit of ${creditOf(confirmRemovePlayer)}. Only staff with transaction authority should finalize this removal to ensure a proper cashout. Are you sure you want to proceed?
                </p>
              ) : (
                <p style={{ marginBottom: "1.5rem", color: "var(--game-text-secondary)" }}>
                  Confirm <strong style={{ color: "#fff" }}>{confirmRemovePlayer.name}</strong> has no chips remaining before removing. If they are cashing out, it is recommended to use the transaction feature.
                </p>
              )}

              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  type="button"
                  className="game-btn game-btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() => setConfirmRemovePlayer(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--danger"
                  style={{ flex: 1 }}
                  disabled={isSubmitting}
                  onClick={handleRemoveConfirm}
                >
                  {isSubmitting ? "Removing…" : "Remove Player"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
